import type { QueryClient } from '@tanstack/react-query'

import {
  searchPlaceAutocomplete,
  PLACE_AUTOCOMPLETE_DEFAULT_LIMIT,
  PLACE_AUTOCOMPLETE_MIN_CHARS,
} from '@/lib/locations/locationiq-autocomplete.service'
import type { PlaceAutocompleteSearchParams, PlaceAutocompleteSearchResult } from '@/lib/locations/locationiq.types'

export const PLACE_AUTOCOMPLETE_QUERY_ROOT = ['locationiq', 'autocomplete'] as const
export const PLACE_AUTOCOMPLETE_MAX_CACHED_QUERIES = 20

/** 15 minutes — autocomplete results rarely change while typing/backspacing. */
export const PLACE_AUTOCOMPLETE_STALE_MS = 15 * 60 * 1000

export type PlaceAutocompleteQueryParams = {
  q: string
  countrycodes?: string
  limit?: number
  acceptLanguage?: string
  tag?: string
  layers?: string
}

export const placeAutocompleteKeys = {
  all: PLACE_AUTOCOMPLETE_QUERY_ROOT,
  search: (params: PlaceAutocompleteQueryParams) =>
    [...PLACE_AUTOCOMPLETE_QUERY_ROOT, normalizeAutocompleteKeyParams(params)] as const,
}

function normalizeAutocompleteKeyParams(params: PlaceAutocompleteQueryParams) {
  const q = params.q.trim().replace(/\s+/g, ' ').toLowerCase()
  return {
    q,
    countrycodes: (params.countrycodes ?? '').toLowerCase(),
    limit: params.limit ?? PLACE_AUTOCOMPLETE_DEFAULT_LIMIT,
    acceptLanguage: (params.acceptLanguage ?? 'en').toLowerCase(),
    tag: params.tag ?? '',
    layers: params.layers ?? '',
  }
}

export async function fetchPlaceAutocompleteQuery(
  params: PlaceAutocompleteQueryParams,
): Promise<PlaceAutocompleteSearchResult> {
  const q = params.q.trim()
  if (q.length < PLACE_AUTOCOMPLETE_MIN_CHARS) {
    return { suggestions: [], query: q }
  }

  const searchParams: PlaceAutocompleteSearchParams = {
    q,
    countrycodes: params.countrycodes,
    limit: params.limit,
    acceptLanguage: params.acceptLanguage,
    tag: params.tag,
    layers: params.layers,
  }

  return searchPlaceAutocomplete(searchParams)
}

/** Keep TanStack Query cache to the last N distinct autocomplete searches. */
export function prunePlaceAutocompleteQueryCache(queryClient: QueryClient) {
  const cache = queryClient.getQueryCache()
  const entries = cache.getAll().filter((entry) => {
    const key = entry.queryKey
    return key[0] === PLACE_AUTOCOMPLETE_QUERY_ROOT[0] && key[1] === PLACE_AUTOCOMPLETE_QUERY_ROOT[1]
  })

  if (entries.length <= PLACE_AUTOCOMPLETE_MAX_CACHED_QUERIES) return

  entries
    .sort((a, b) => (a.state.dataUpdatedAt ?? 0) - (b.state.dataUpdatedAt ?? 0))
    .slice(0, entries.length - PLACE_AUTOCOMPLETE_MAX_CACHED_QUERIES)
    .forEach((entry) => cache.remove(entry))
}
